import { effect, inject, Injectable, signal } from '@angular/core';
import { IOpenAiModel } from '../../../interfaces/i-open-ai-model';
import { IWorkflowExecution, IWorkflowInstructionExecution } from "../interfaces/i-automation";
import { IInstructions } from "../../automation/interfaces/i-instructions";
import { IWorkflow } from "../../automation/interfaces/i-workflow";
import { ChatSessionService } from "./chat-session.service";

interface IAutomationSessionState {
  isWorkflowInProgress: boolean;
  isWaitingForUserInput: boolean;
  isWaitingForFileUpload: boolean;
  isFileUploadInProgress: boolean;
  workflowExecutionState: IWorkflowExecution | null;
  instructionsExecutionState: IWorkflowInstructionExecution | null;
  instructionInProgress: IInstructions | null;
  workflowBeingExecuted: IWorkflow | null;
  currentInstructionIndex: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class AutomationStateService {
  readonly #chatSessionService = inject(ChatSessionService);

  /**
   * Holds per-session automation state so that multiple workflows can execute
   * concurrently in different chat sessions without clobbering each other.
   *
   * The public signals below (isWorkflowInProgress, etc.) represent the
   * automation state for the *currently selected* chat session only.
   */
  #sessionStates = signal<Map<string | number, IAutomationSessionState>>(new Map());

  /**
   * Indicates whether a workflow execution is currently in progress.
   */
  isWorkflowInProgress = signal(false);

  /**
   * Indicates whether the system is waiting for user input.
   */
  isWaitingForUserInput = signal(false);

  /**
   * Indicates whether the system is waiting for user to attach files to the message.
   */
  isWaitingForFileUpload = signal(false);

  /**
   * Indicates whether a file upload is currently in progress.
   */
  isFileUploadInProgress = signal(false);

  /**
   * Holds the current workflow execution process state.
   * This is used to track the execution of a workflow and its associated chat session.
   */
  workflowExecutionState = signal<IWorkflowExecution | null>(null);

  /**
   * Holds the current instruction execution state.
   * This is used to track the execution of instructions within a workflow.
   */
  instructionsExecutionState = signal<IWorkflowInstructionExecution | null>(null);

  /**
   * Holds the current instruction that is in progress.
   * This is used to track the instruction that is currently being executed or processed.
   */
  instructionInProgress = signal<IInstructions | null>(null);

  /**
   * Holds the model chosen specifically within the StartConversation route.
   * This prevents global `instructionInProgress` state from leaking into
   * the starter conversation context while still allowing explicit selection
   * in that route to drive the model.
   */
  starterConversationInstructionModel = signal<IOpenAiModel | null>(null);

  /**
   * The workflow that is currently in progress.
   */
  workflowBeingExecuted = signal<IWorkflow | null>(null);

  /**
   * The index of the current instruction being processed in the workflow execution.
   */
  currentInstructionIndex = signal<number | null>(null);

  /**
   * Set or clear the StartConversation-scoped instruction model.
   */
  setStarterInstructionModel(model: IOpenAiModel | null): void {
    this.starterConversationInstructionModel.set(model);
  }

  constructor() {
    // Synchronise the global signals with per-session state based on the
    // currently selected chat session. This allows the UI to continue using
    // the existing signals API while enabling true per-session storage.
    //
    // IMPORTANT: This effect only runs when selectedSessionId changes because that's
    // the only signal we read. We don't read the other signals here to prevent
    // the effect from running on every signal change. Signal changes are saved
    // via updateSessionState() instead.
    effect(() => {
      const selectedSessionId = this.#chatSessionService.selectedSessionId();

      if (!selectedSessionId) {
        return;
      }

      // Load state for the selected session (or create default if new session)
      const nextState = this.#sessionStates().get(selectedSessionId) ?? this.#createDefaultState();
      
      // If this is a new session without existing state, store the defaults
      if (!this.#sessionStates().get(selectedSessionId)) {
        this.#setSessionStateInternal(selectedSessionId, nextState);
      }
      
      // Apply the session's state to global signals
      this.#applyStateToSignals(nextState);
    });
  }

  /**
   * Returns the automation state for the specified session, if any.
   */
  getSessionState(sessionId: string | number): IAutomationSessionState | undefined {
    return this.#sessionStates().get(sessionId);
  }

  /**
   * Updates the automation state for the specified session with the provided
   * partial values. If the session does not yet have state, a default entry
   * is created first.
   */
  updateSessionState(
    sessionId: string | number,
    state: Partial<IAutomationSessionState>
  ): void {
    this.#sessionStates.update((current) => {
      const updated = new Map(current);
      const previous = updated.get(sessionId) ?? this.#createDefaultState();
      updated.set(sessionId, { ...previous, ...state });
      return updated;
    });

    const selectedSessionId = this.#chatSessionService.selectedSessionId();
    if (selectedSessionId === sessionId) {
      const nextState = this.#sessionStates().get(sessionId);
      if (nextState) {
        this.#applyStateToSignals(nextState);
      }
    }
  }

  /**
   * Clears all automation state for the specified session.
   */
  clearSessionState(sessionId: string | number): void {
    this.#sessionStates.update((current) => {
      const updated = new Map(current);
      updated.delete(sessionId);
      return updated;
    });

    const selectedSessionId = this.#chatSessionService.selectedSessionId();
    if (selectedSessionId === sessionId) {
      const defaults = this.#createDefaultState();
      this.#applyStateToSignals(defaults);
    }
  }

  #createDefaultState(): IAutomationSessionState {
    return {
      isWorkflowInProgress: false,
      isWaitingForUserInput: false,
      isWaitingForFileUpload: false,
      isFileUploadInProgress: false,
      workflowExecutionState: null,
      instructionsExecutionState: null,
      instructionInProgress: null,
      workflowBeingExecuted: null,
      currentInstructionIndex: null,
    };
  }

  #setSessionStateInternal(
    sessionId: string | number,
    state: IAutomationSessionState
  ): void {
    this.#sessionStates.update((current) => {
      const updated = new Map(current);
      updated.set(sessionId, state);
      return updated;
    });
  }

  #applyStateToSignals(state: IAutomationSessionState): void {
    this.isWorkflowInProgress.set(state.isWorkflowInProgress);
    this.isWaitingForUserInput.set(state.isWaitingForUserInput);
    this.isWaitingForFileUpload.set(state.isWaitingForFileUpload);
    this.isFileUploadInProgress.set(state.isFileUploadInProgress);
    this.workflowExecutionState.set(state.workflowExecutionState);
    this.instructionsExecutionState.set(state.instructionsExecutionState);
    this.instructionInProgress.set(state.instructionInProgress);
    this.workflowBeingExecuted.set(state.workflowBeingExecuted);
    this.currentInstructionIndex.set(state.currentInstructionIndex);
  }
}
